import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import Chart from "chart.js/auto";
import { MultiSelectComponent } from "@merchant-webapp/components";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  StackedBarChartConfig,
  StackedBarChartLabels,
} from "./stacked-bar-chart.model";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { formatNumber } from "../../utils/number.util";

@Component({
  selector: "er-stacked-bar-chart",
  templateUrl: "./stacked-bar-chart.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslocoPipe,
  ],
})
export class StackedBarChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() config!: StackedBarChartConfig;
  @Input() labels!: StackedBarChartLabels;
  @Input() hasLineChart!: boolean;

  private readonly translocoService = inject(TranslocoService);
  private translatedLabels!: string[];

  public chart!: Chart;
  public selectedItems: string[] = [];

  ngOnInit() {
    this.init();
    this.initTranslateSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.["config"]?.currentValue) {
      this.init();
    }
  }

  private init() {
    this.initTranslatedLabels();
    this.initChartData();
  }

  private initTranslateSubscription() {
    if (this.config?.items?.length) {
      this.translocoService
        .selectTranslate(this.config?.items[0]?.label)
        .subscribe(() => {
          this.init();
        });
    }
  }

  private initTranslatedLabels() {
    this.translatedLabels = [];
    this.config?.items?.forEach((item) => {
      this.translatedLabels.push(this.translocoService.translate(item.label));
    });
  }

  public initChartData() {
    if (this.config) {
      this.selectedItems = this.config?.items?.map((item) => item.value);
      this.initChart();
    }
  }

  public changeSelectedItems(selectedItems: string[]) {
    if (!selectedItems.length) return;

    this.selectedItems = selectedItems;
    this.chart.destroy();
    this.initChart();
  }

  private initChart() {
    const data = {
      labels: this.config?.labels,
      datasets: this.getDatasets(),
      dates: this.config?.dates || [],
    };

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart("canvas-stacked-bar-chart", {
      type: "bar",
      data: data,
      options: {
        plugins: {
          legend: {
            display: false,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              callback: (value) =>
                data?.labels?.length === 1
                  ? data?.labels[Number(value)] + " ".repeat(45)
                  : [
                      data?.labels[Number(value)],
                      data.dates.length ? data?.dates[Number(value)] : "",
                    ],
              font: {
                family: "Ubuntu",
                size: 12,
                weight: 400,
              },
              stepSize: 500,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) =>
                this.config.formatNumberYAxis
                  ? `${formatNumber(Number(value))} ${this?.config?.measure}`
                  : Number(value),
              padding: 20,
              font: {
                family: "Ubuntu",
                size: 10,
                weight: 400,
              },
            },
            suggestedMax: 3000,
            border: {
              color: "transparent",
            },
          },
        },
      },
    });
  }

  private getLineDatasets() {
    const datasets: any[] = [];

    this.config?.datasets?.forEach((item, index) => {
      const itemColor = this.config?.colors[index];

      if (this.selectedItems.includes(item["name"] as string)) {
        datasets.push({
          type: "line" as const,
          label: this.translatedLabels[index],
          data: item["data"],
          backgroundColor: itemColor,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: itemColor,
          pointRadius: 10,
          pointHoverRadius: 12,
          borderWidth: 4,
          pointBorderWidth: 4,
          borderColor: itemColor,
        });
      }
    });

    return datasets;
  }

  private getBarDatasets() {
    const datasets: any[] = [];

    this.config?.datasets?.forEach((item, index) => {
      const itemColor = this.config?.colors[index];

      if (this.selectedItems.includes(item["name"] as string)) {
        datasets.push({
          type: "bar" as const,
          label: this.translatedLabels[index],
          data: item["data"],
          backgroundColor: itemColor,
          maxBarThickness: 38,
          barPercentage: 0.9,
          categoryPercentage: 0.4,
          borderRadius: 6,
        });
      }
    });

    return datasets;
  }

  private getDatasets() {
    const datasets = [];

    if (this.hasLineChart) {
      datasets.push(...this.getLineDatasets());
    }

    datasets.push(...this.getBarDatasets());

    return datasets;
  }

  ngOnDestroy(): void {
    this.chart.destroy();
  }
}
